import { useMemo, useState, useEffect } from 'react';
import { usePersistentCallback } from '../hooks';
import { useCacheManager } from './context';
/**
 * Creates a hook for caching data with configurable TTL and prefix.
 * This hook provides a stable cache interface that will never trigger re-renders when accessing or updating the cache.
 *
 * Use cases:
 * - Storing computed values that are expensive to recalculate
 * - Persisting UI state that needs to survive component unmounts
 */
export function createCacheHook({ ttl: defaultTTL, prefix = '' } = {}) {
    return function useCache(localPrefix) {
        const cacheManager = useCacheManager();
        const _prefix = localPrefix ? [prefix, localPrefix].filter(Boolean) : prefix ? [prefix] : [];
        const getCacheKey = (key) => {
            const keyAsArray = Array.isArray(key) ? key : [key];
            return [..._prefix, ...keyAsArray];
        };
        const get = usePersistentCallback((key) => {
            return cacheManager.get(getCacheKey(key));
        });
        const set = usePersistentCallback((key, value, ttl) => {
            cacheManager.set(getCacheKey(key), value, ttl);
        });
        const getOrCompute = usePersistentCallback((key, fn, ttl) => {
            return cacheManager.getOrCompute(getCacheKey(key), fn, ttl !== null && ttl !== void 0 ? ttl : defaultTTL);
        });
        const remove = usePersistentCallback((key) => {
            cacheManager.remove(getCacheKey(key));
        });
        const clear = usePersistentCallback(() => {
            if (_prefix.length > 0) {
                cacheManager.clear((key) => {
                    if (!Array.isArray(key) || key.length < _prefix.length) {
                        return false;
                    }
                    return _prefix.every((prefixPart, i) => key[i] === prefixPart);
                });
            }
            else {
                cacheManager.clear();
            }
        });
        return useMemo(() => {
            return {
                get,
                set,
                getOrCompute,
                remove,
                clear
            };
        }, [get, set, getOrCompute, remove, clear]);
    };
}
const useStateCacheHook = createCacheHook({ prefix: 'cached-state' });
/**
 * A hook that provides persistent state storage across component remounts.
 * The state is cached using the provided key and persists even when the component unmounts.
 *
 * @template TData - The type of the state value
 * @param key - A unique string identifier used to store and retrieve the cached state
 * @param defaultValue - Optional initial value used when no cached value exists for the key
 * @returns A tuple containing the current state and a setter function, similar to `useState`
 *
 * @example
 * ```tsx
 * // Basic usage - persists collapse state across remounts
 * const [collapsed, setCollapsed] = useCachedState('sidebar-collapsed', false);
 *
 * // With dynamic key - useful for per-item state
 * const [expanded, setExpanded] = useCachedState(`item-${itemId}-expanded`, true);
 * ```
 */
export function useCachedState(key, defaultValue) {
    var _a;
    const { get, set } = useStateCacheHook(key);
    const [state, _setState] = useState((_a = get(key)) !== null && _a !== void 0 ? _a : defaultValue);
    // when there is no value in cache then default value becomes our state
    useEffect(() => {
        if (get(key) === undefined) {
            _setState(defaultValue);
        }
    }, [defaultValue, get, key, _setState]);
    const setState = usePersistentCallback((value) => {
        _setState(value);
        set(key, value);
    });
    return [state, setState];
}
